/** Bill entry for a scanned member: total, receipt, lines, coupon/reservation, lunch pack, ISIC, submit. */
import type { LunchPack, PosScanResult, PosTransactionRequest } from "../api-types";
import { startQrScan, type ScanSession } from "../camera-qr-scanner";
import { errorBox, errorMessage, field, h } from "../dom-helpers";
import type { RenderedScreen, ScannerApp } from "../scanner-app-context";
import { staffApi } from "../staff-api-client";
import { strings } from "../strings";
import { submitTransaction } from "../transaction-submitter";
import { renderMemberSummary } from "./member-summary-card";
import { createReceiptLinesEditor } from "./receipt-lines-editor";

export function renderBill(app: ScannerApp, member: PosScanResult): RenderedScreen {
  const till = app.till;
  if (!till) throw new Error("Bill screen requires a till");
  const error = errorBox();
  const total = h("input", { name: "total", type: "number", min: "0", step: "1", inputMode: "numeric", required: true });
  const receipt = h("input", { name: "receipt", autocomplete: "off", required: true });
  const lines = createReceiptLinesEditor();

  // Coupon: "" = none, "r:<reservationId>" = held reservation, "c:<couponId>" = direct redeem.
  const coupon = h("select", { name: "coupon" }, h("option", { value: "" }, strings.billNoCoupon));
  const addReservationOption = (reservationId: string, couponId: string) => {
    const value = `r:${reservationId}`;
    if (!coupon.querySelector(`option[value="${CSS.escape(value)}"]`)) {
      const title = member.availableCoupons.find((c) => c.id === couponId)?.title ?? couponId;
      coupon.append(h("option", { value }, `${strings.memberReservation}: ${title}`));
    }
    coupon.value = value;
  };
  for (const c of member.availableCoupons) coupon.append(h("option", { value: `c:${c.id}` }, `${c.value} · ${c.title}`));
  if (member.reservation) addReservationOption(member.reservation.id, member.reservation.couponId);

  // Reservation QR (GPR1…) scanned at the bill: resolved through /pos/scans, must belong to this member.
  const video = h("video", { className: "camera", hidden: true, muted: true });
  let session: ScanSession | null = null;
  const stopCamera = () => {
    session?.stop();
    session = null;
    video.hidden = true;
  };
  const scanReservation = h("button", { type: "button", className: "secondary" }, strings.billScanReservation);
  scanReservation.addEventListener("click", async () => {
    if (session) return stopCamera();
    error.show(null);
    video.hidden = false;
    try {
      session = await startQrScan(video, (text) => {
        stopCamera();
        void (async () => {
          try {
            const scanned = await staffApi.scan({ tillId: till.tillId, brandId: till.brandId, mallId: till.mallId, credential: { qrToken: text } });
            if (scanned.memberId !== member.memberId || !scanned.reservation) error.show(strings.billReservationOther);
            else addReservationOption(scanned.reservation.id, scanned.reservation.couponId);
          } catch (e) {
            if (!app.handleAuthError(e)) error.show(errorMessage(e));
          }
        })();
      });
    } catch {
      stopCamera();
      error.show(strings.scanCameraUnavailable);
    }
  });

  // Lunch pack sale (D7) and meal use from an owned or just-bought pack.
  const packSale = h("select", { name: "packSale", disabled: true }, h("option", { value: "" }, strings.billPackNone));
  const packNote = h("p", { className: "hint", hidden: true }, strings.billPackUnavailable);
  const mealUsed = h("input", { type: "checkbox", name: "mealUsed" });
  const ownedHasMeals = !!member.ownedPack && member.ownedPack.used < member.ownedPack.meals;
  const syncMealUsed = () => {
    mealUsed.disabled = !ownedHasMeals && !packSale.value;
    if (mealUsed.disabled) mealUsed.checked = false;
  };
  packSale.addEventListener("change", syncMealUsed);
  syncMealUsed();
  staffApi
    .listLunchPacks()
    .then((packs: LunchPack[]) => {
      packSale.append(...packs.map((p) => h("option", { value: p.id }, strings.billPackOption(p.meals, p.priceCzk))));
      packSale.disabled = packs.length === 0;
      packNote.hidden = packs.length > 0;
    })
    .catch((e: unknown) => {
      console.warn("lunch pack catalogue unavailable", e);
      packNote.hidden = false;
    });

  // ISIC verified by staff (D8): saved immediately, reverted on failure.
  const isic = h("input", { type: "checkbox", name: "isic", checked: member.isicVerified });
  const isicStatus = h("span", { className: "hint" });
  isic.addEventListener("change", async () => {
    const wanted = isic.checked;
    isic.disabled = true;
    isicStatus.textContent = strings.billIsicSaving;
    try {
      await staffApi.setIsic(member.memberId, wanted);
      member.isicVerified = wanted;
      isicStatus.textContent = "";
    } catch (e) {
      isic.checked = !wanted;
      isicStatus.textContent = app.handleAuthError(e) ? "" : `${strings.billIsicFailed} ${errorMessage(e)}`;
    } finally {
      isic.disabled = false;
    }
  });

  const submit = h("button", { type: "submit", className: "primary big" }, strings.billSubmit);
  const onSubmit = async (event: Event) => {
    event.preventDefault();
    error.show(null);
    const totalCzk = Number(total.value);
    if (!total.value.trim() || !Number.isInteger(totalCzk) || totalCzk < 0) return error.show(strings.billInvalidTotal);
    if (!receipt.value.trim()) return error.show(strings.billInvalidReceipt);
    const lineItems = lines.read();
    if (!lineItems) return error.show(strings.billInvalidLine);

    const request: PosTransactionRequest = {
      tillId: till.tillId,
      brandId: till.brandId,
      mallId: till.mallId,
      memberId: member.memberId,
      receiptNo: receipt.value.trim(),
      totalCzk,
      lines: lineItems,
      reservationId: coupon.value.startsWith("r:") ? coupon.value.slice(2) : null,
      couponId: coupon.value.startsWith("c:") ? coupon.value.slice(2) : null,
      lunchPackPurchaseId: packSale.value || null,
      lunchPackMealUsed: mealUsed.checked,
      occurredAt: new Date().toISOString(),
    };
    submit.disabled = true;
    submit.textContent = strings.billSubmitting;
    try {
      const outcome = await submitTransaction(request);
      app.navigate({ name: "result", result: outcome.result, queued: outcome.queued });
    } catch (e) {
      if (!app.handleAuthError(e)) error.show(errorMessage(e));
      submit.disabled = false;
      submit.textContent = strings.billSubmit;
    }
  };

  const form = h(
    "form",
    { className: "card stack", onsubmit: (e: Event) => void onSubmit(e) },
    h("h2", {}, strings.billTitle),
    h("div", { className: "row" }, field(strings.billTotal, total), field(strings.billReceipt, receipt)),
    lines.el,
    field(strings.billCoupon, coupon),
    scanReservation,
    video,
    field(strings.billPackSale, packSale),
    packNote,
    h("label", { className: "toggle" }, mealUsed, strings.billPackMealUsed),
    h("label", { className: "toggle" }, isic, strings.billIsicVerified, isicStatus),
    error.el,
    submit,
    h("button", { type: "button", className: "ghost", onclick: () => app.navigate({ name: "scan" }) }, strings.billCancel),
  );

  return { el: h("div", { className: "stack" }, renderMemberSummary(member, app.brands), form), dispose: stopCamera };
}
